import * as THREE from "three";
import type { FireResult, Weapon, WeaponHudState } from "./weapon";
import type { ProjectileManager } from "./projectile";
import { bus } from "../core/bus";
import { Audio } from "../sfx/audio";
import type { WeaponMods } from "../meta/loadout";

const BASE_ROUNDS_PER_SEC = 1.5;
const BASE_BLAST_RADIUS = 14;
// Two core hits (or a core hit plus a rim graze) kill 3-HP infantry — the
// drum weapon wounds hard per shell without matching the howitzer's one-shot.
const BLAST_DAMAGE = 2;
const SPEED = 600;
const MAG_SIZE = 6;
const RELOAD_TIME = 3;

export class Autocannon implements Weapon {
  readonly id = "autocannon";
  readonly label = "30MM GAU-23/A";
  ammo = MAG_SIZE;
  readonly maxAmmo = MAG_SIZE;
  readonly infiniteAmmo = false;
  readonly projectileSpeed = SPEED;

  private cooldownLeft = 0;
  private reloadLeft = 0;
  private readonly cooldown: number;
  private readonly blastRadius: number;

  constructor(
    private readonly projectiles: ProjectileManager,
    mods?: Pick<WeaponMods, "cooldownMult" | "blastRadiusMult">,
  ) {
    this.cooldown = (1 / BASE_ROUNDS_PER_SEC) * (mods?.cooldownMult ?? 1);
    this.blastRadius = BASE_BLAST_RADIUS * (mods?.blastRadiusMult ?? 1);
  }

  get isReady(): boolean {
    return this.cooldownLeft <= 0 && this.reloadLeft <= 0 && this.ammo > 0;
  }

  getHudState(): WeaponHudState {
    const reloading = this.reloadLeft > 0;
    const cycling = !reloading && this.cooldownLeft > 0;
    return {
      weaponId: "autocannon",
      modeLabel: reloading ? "RELOAD" : "BURST",
      primaryLabel: reloading ? "DRUM RELOAD" : "FIRE CYCLE",
      primaryValue: reloading
        ? 1 - this.reloadLeft / RELOAD_TIME
        : cycling
          ? 1 - this.cooldownLeft / this.cooldown
          : 1,
      primaryText: reloading ? `${this.reloadLeft.toFixed(1)}S` : cycling ? `${this.cooldownLeft.toFixed(2)}S` : "READY",
      secondaryLabel: "DRUM",
      secondaryText: `${this.ammo}/${this.maxAmmo} ROUNDS`,
      statusText: reloading ? "RELOADING" : cycling ? "CYCLING" : "READY",
      shortText: reloading ? `REL ${this.reloadLeft.toFixed(1)}S` : `${this.ammo}/${this.maxAmmo}`,
      zoomText: "MEDIUM",
    };
  }

  update(dt: number): void {
    if (this.cooldownLeft > 0) this.cooldownLeft = Math.max(0, this.cooldownLeft - dt);
    if (this.reloadLeft > 0) {
      this.reloadLeft = Math.max(0, this.reloadLeft - dt);
      if (this.reloadLeft <= 0) this.ammo = this.maxAmmo;
    }
  }

  fire(origin: THREE.Vector3, dir: THREE.Vector3): FireResult {
    if (!this.isReady) return { fired: false, hitPoint: null, hitObject: null };
    this.ammo -= 1;
    this.cooldownLeft = this.cooldown;
    if (this.ammo <= 0) this.reloadLeft = RELOAD_TIME;
    this.projectiles.spawn(origin, dir, SPEED, this.blastRadius, this.id, BLAST_DAMAGE);
    Audio.cannonShot();
    bus.emit("weapon:fired", { id: this.id });
    return { fired: true, hitPoint: null, hitObject: null };
  }
}
